import PortalIdParser from 'PortalIdParser';
import * as CRMObjectTypes from 'automation-ui-references/object-types/CRMObjectTypes';
import { isCustomObject } from 'automation-ui-references/object-types/objectTypeUtils';
/* Ads */

export const adAudiencesUrl = audienceId => `/ads/${PortalIdParser.get()}/audiences/${audienceId}`;
export const adsOnboardingUrl = () => `/ads/${PortalIdParser.get()}/onboarding`;

/* Chatflow */

export const chatflowUrl = (chatflowId, chatflowSource) => {
  const portalId = PortalIdParser.get();
  const CHATFLOW_LIVE_CHAT = 'LIVE_CHAT';
  const CHATFLOW_FB_MESSENGER = 'FB_MESSENGER';
  const chatflowSourceMap = {
    [CHATFLOW_LIVE_CHAT]: 'live-chat',
    [CHATFLOW_FB_MESSENGER]: 'messenger'
  };
  const source = chatflowSourceMap[chatflowSource];
  return `/chatflows/${portalId}/edit/${source}/${chatflowId}/build`;
};

/* Email */

export const createNewEmailUrl = () => {
  const redirectName = 'Workflows';
  const emailType = 'AUTOMATED';
  const portalId = PortalIdParser.get();
  const redirectUrl = encodeURIComponent(window.location.href);
  return `/content/${portalId}/create/email?redirectName=${redirectName}&redirectUrl=${redirectUrl}&emailType=${emailType}`;
};
export const emailSettingsUrl = () => {
  const portalId = PortalIdParser.get();
  return `/settings/${portalId}/marketing/email/configuration`;
};
export const emailUrl = emailId => {
  const portalId = PortalIdParser.get();
  return `/email/${portalId}/details/${emailId}/`;
};

/* Event */

export const eventUrl = eventId => {
  const portalId = PortalIdParser.get();
  return `/reports/${portalId}/events/${eventId}`;
};

/* Form */

export const formUrl = formId => {
  const portalId = PortalIdParser.get();
  return `/forms/${portalId}/${formId}/edit`;
};

/* Integrations */

export const integrationSettingsUrl = () => `/integrations-settings/${PortalIdParser.get()}/installed`;

/* Lists */

export const listUrl = listId => {
  const portalId = PortalIdParser.get();
  return `/lists/${portalId}/list/${listId}/`;
};

/* Settings */

export const adAccountSettingsUrl = () => `/settings/${PortalIdParser.get()}/marketing/ads/adAccounts`;
export const userSettingsUrl = () => `/settings/${PortalIdParser.get()}/users`;
export const associationLabelSettingsUrl = targetObjectTypeId => `/association-settings/${PortalIdParser.get()}/?type=${targetObjectTypeId}`;
export const paidUserSettingsUrl = () => {
  return `${userSettingsUrl()}/paid-users`;
};
export const notificationsSettingsUrl = (highlight, tab) =>
// https://product.hubteam.com/docs/notifications/preferences/overview.html#deep-linking-to-notification-default-settings
`/notification-preferences/${PortalIdParser.get()}?highlight=${highlight}&tab=${tab}`;

/* Upgrade */

export const upgradeToWorkflows = () => {
  const portalId = PortalIdParser.get();
  return `/upgrade/${portalId}/workflows`;
};

/* CRM */

/* CRM > ObjectUrl */

const companyObjectUrl = companyId => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/company/${companyId}`;
};
const contactObjectUrl = contactId => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/contact/${contactId}`;
};
const customObjectUrl = (objectId, objectTypeId) => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/record/${objectTypeId}/${objectId}`;
};
const dealObjectUrl = dealId => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/deal/${dealId}`;
};
const quoteObjectUrl = quoteId => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/quote/${quoteId}`;
};
const ticketObjectUrl = ticketId => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/ticket/${ticketId}`;
};
const conversationUrl = conversationId => {
  const portalId = PortalIdParser.get();
  return `/live-messages/${portalId}?cvObjectId=${conversationId}`;
};
const leadUrl = leadId => {
  const portalId = PortalIdParser.get();
  return `/prospecting/${portalId}/leads?leadId=${leadId}`;
};
const surveyPathByType = {
  CUSTOM: 'custom',
  NPS: 'loyalty',
  CES: 'support',
  CSAT: 'satisfaction'
};
const feedbackSubmissionUrl = properties => {
  const portalId = PortalIdParser.get();
  const surveyType = properties.hs_survey_type;
  const formId = properties.hs_survey_id;
  const submissionId = properties.hs_ingestion_id;
  let surveyPath;
  if (surveyType) {
    surveyPath = surveyPathByType[surveyType.value];
  }
  if (surveyType && formId && submissionId && surveyPath) {
    return `/feedback/${portalId}/${surveyPath}/${formId.value}/submissions/${submissionId.value}`;
  }
  return null;
};
const goalsUrl = (goalId, properties) => {
  const portalId = PortalIdParser.get();
  let goalType = properties.hs_goal_type;
  if (goalType && goalType.value === 'standard_object_custom_goal' && properties.hs_kpi_single_object_custom_goal_type_name) {
    goalType = properties.hs_kpi_single_object_custom_goal_type_name;
  }
  if (goalType && goalType.value !== 'standard_object_custom_goal') {
    return `/goals/${portalId}/manage/${goalId}?goalType=${goalType.value}`;
  }
  return null;
};
/* Here for when we need it */
/*
const conversationThreadUrl = threadId => {
  const portalId = PortalIdParser.get();
  return `/live-messages/${portalId}/inbox/${threadId}`;
};
*/

/* CRM Listing poges */

export const listingPageUrl = objectTypeId => {
  const portalId = PortalIdParser.get();
  return `/contacts/${portalId}/objects/${objectTypeId}/views/all/list`;
};

/* CRM > Task board */

export const taskBoardUrl = taskId => {
  const portalId = PortalIdParser.get();
  const baseUrl = `/contacts/${portalId}/tasks/board/view/all/`;
  if (taskId) {
    return `${baseUrl}?taskId=${taskId}`;
  }
  return baseUrl;
};

/* CRM > List */

export const contactListUrl = listId => `/contacts/${PortalIdParser.get()}/lists/${listId}`;
export const objectListUrl = listId => `/contacts/${PortalIdParser.get()}/objectLists/${listId}`;

/* CRM > Property Settings */

export const editPropertyUrl = (objectTypeId, propertyName) => `/property-settings/${PortalIdParser.get()}/properties?action=edit&property=${encodeURIComponent(propertyName)}&type=${encodeURIComponent(objectTypeId)}`;

/* CRM > InboundDb Object */

export const inboundDbObjectUrlByObjectTypeId = (objectTypeId, objectId, properties = {}) => {
  if (isCustomObject(objectTypeId)) {
    return customObjectUrl(objectId, objectTypeId);
  }
  switch (objectTypeId) {
    case CRMObjectTypes.DEAL_ID:
      return dealObjectUrl(objectId);
    case CRMObjectTypes.TICKET_ID:
      return ticketObjectUrl(objectId);
    case CRMObjectTypes.CONTACT_ID:
      return contactObjectUrl(objectId);
    case CRMObjectTypes.COMPANY_ID:
      return companyObjectUrl(objectId);
    case CRMObjectTypes.QUOTE_ID:
      return quoteObjectUrl(objectId);
    case CRMObjectTypes.CONVERSATION_ID:
      return conversationUrl(objectId);
    case CRMObjectTypes.FEEDBACK_SUBMISSION_ID:
      return feedbackSubmissionUrl(properties);
    case CRMObjectTypes.GOAL_TARGET_ID:
      return goalsUrl(objectId, properties);
    case CRMObjectTypes.LEAD_ID:
      return leadUrl(objectId);
    default:
      return null;
  }
};
export const inboundDbObjectPropertiesUrl = (objectTypeId, objectId) => {
  const baseUrl = inboundDbObjectUrlByObjectTypeId(objectTypeId, objectId);
  return baseUrl ? `${baseUrl}/properties` : null;
};
export const inboundDbObjectPropertyUrl = (propertyName, objectTypeId, objectId) => {
  const baseUrl = inboundDbObjectPropertiesUrl(objectTypeId, objectId);
  return baseUrl ? `${baseUrl}?search=${propertyName}` : null;
};
export const inboundDbObjectTimelineUrl = (objectTypeId, objectId) => {
  switch (objectTypeId) {
    case CRMObjectTypes.DEAL_ID:
    case CRMObjectTypes.TICKET_ID:
    case CRMObjectTypes.CONTACT_ID:
    case CRMObjectTypes.COMPANY_ID:
      return inboundDbObjectUrlByObjectTypeId(objectTypeId, objectId);
    default:
      return null;
  }
};
export const inboundDbObjectPropertyHistoryUrl = (objectTypeId, objectId) => {
  switch (objectTypeId) {
    case CRMObjectTypes.DEAL_ID:
    case CRMObjectTypes.TICKET_ID:
    case CRMObjectTypes.CONTACT_ID:
    case CRMObjectTypes.COMPANY_ID:
      return `${inboundDbObjectUrlByObjectTypeId(objectTypeId, objectId)}/history`;
    default:
      return null;
  }
};

/* CRM > Recycle Bin */

export const recycleBinUrl = objectTypeId => {
  const portalId = PortalIdParser.get();
  const baseUrl = `/recycling-bin/${portalId}/restore`;
  switch (objectTypeId) {
    case CRMObjectTypes.CONTACT_ID:
      return `${baseUrl}/contact`;
    case CRMObjectTypes.DEAL_ID:
      return `${baseUrl}/deal`;
    case CRMObjectTypes.TICKET_ID:
      return `${baseUrl}/ticket`;
    case CRMObjectTypes.COMPANY_ID:
      return `${baseUrl}/company`;
    // Dev note: quotes and custom objects do not have recycle bins
    default:
      return null;
  }
};